import { useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { flatJoin, safeParseInt, setDocumentTitle } from "shared/util"

import { useTenant } from "features/tenant/context"

import {
	Alert, Box, Container, ContainerProps, Paper, Stack
} from "@mui/material"

import { ErrorInfo } from "components/controls/errorInfo"
import { Loading } from "components/controls/loading"

import { SearchTableFooter, SearchTableHeader } from "components/controls/searchTable"


import { useGetConvention } from "interface/convention"

import { ConventionsSessionsBody } from "./components/body"
import { useConventionPage } from "./container"

import { KojinPreviewDialog } from "features/kojin/dialog/preview"
import { SisetuPreviewDialog } from "features/sisetu/dialog/preview"
import { useTopBar } from "components/topBar/container"
import { useSideMenu } from "components/sideMenu/container"

type ConventionPageProps = {
  topBarState: ReturnType<typeof useTopBar>
  sideMenuState: ReturnType<typeof useSideMenu>
} & ContainerProps
export const ConventionPage = ({
	topBarState: { setBreadCrumbs },
	sideMenuState: { setActivePage },
	...others
}: ConventionPageProps) => {
	const tenant = useTenant()
  const [params] = useSearchParams()
  const conventionId = safeParseInt(params.get('conventionId')) ?? null
  const conventionState = useGetConvention(conventionId)
	const convention = useMemo(() => conventionState.data, [conventionState])
  const {
    searchWord,
    setSearchWord,
    filteredSessionList,
		selectedKojinCode, onSelectKojin,
		selectedAuthorId,
		selectedSisetuCode,
		closeModal
  } = useConventionPage({ convention })

	useEffect(
    () => {
			const conventionTitle = convention ? flatJoin(' ', convention.numberOfTimes, convention.conventionName) : '学会'
      setBreadCrumbs([{ label: conventionTitle }])
      setDocumentTitle(conventionTitle, tenant.tenantName)
			setActivePage('convention')
    },
    [tenant, convention, setBreadCrumbs, setActivePage]
  )
	if (!conventionId) return <Alert severity="error" sx={{ wordBreak: 'break-all' }}>パラメータ不正</Alert>
  if (conventionState.loading) return <Loading/>
  if (conventionState.error) return <ErrorInfo error={conventionState.error}/>
  if (!convention) return <></>
	
	return (
		<Container
			sx={{ maxHeight: '100%', overflow: 'hidden', padding: 2 }}
			{...others}
		>
			<Paper sx={{ height: '100%', overflow: 'hidden' }}>
				<Stack direction="column" height="100%">
					<Box padding={2}>
						<SearchTableHeader
							title={flatJoin(' ', convention.numberOfTimes, convention.conventionName)}
							searchWord={searchWord}
							setSearchWord={setSearchWord}
						/>
					</Box>

					<ConventionsSessionsBody
						sessionList={filteredSessionList ?? []}
						onClickedSpeaker={onSelectKojin}
					/>

					<Box padding={2}>
						<SearchTableFooter
							listLengthTitle='演題'
							listLength={filteredSessionList?.length ?? 0}
							listLengthUnit='件'
							sourceUrl={convention?.officialUrl ?? ''}
							onListDownload={() => {
								alert('未実装')
							}}
						/>
					</Box>
				</Stack>
			</Paper>

			{ (selectedKojinCode || selectedAuthorId) ? (
				<KojinPreviewDialog
					kojinCode={selectedKojinCode ?? undefined}
					authorId={selectedAuthorId ?? undefined}
					onClose={closeModal}
				/>
			) : selectedSisetuCode ? (
				<SisetuPreviewDialog
					sisetuCode={selectedSisetuCode}
					onClose={closeModal}
				/>
			) : <></>}
		</Container>

	)
}